import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import {
  AutoCompleteModule,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { Subject, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { PostComponent } from '../../components/post/post.component';
import { RecommendedConnectionsComponent } from '../../components/recommended-connections/recommended-connections.component';
import { AISummaryComponent } from '../../components/ai-summary/ai-summary.component';
import { WelcomeMessageComponent } from '../../components/welcome-message/welcome-message.component';
import { JobCardComponent } from '../../components/job-card/job-card.component';
import {
  MockFeedService,
  FeedPost,
  RecommendedConnection,
} from '../../services-mock/mock-feed.service';
import { MockJobService, Job } from '../../services-mock/mock-job.service';
import { AuthService } from '../../services/auth.service';
import { OrganizationService } from '../../services/organization.service';
import { OrganizationDto } from '../../dto/organization.dto';
import { RouterModule } from '@angular/router';

interface FeedItem {
  type: 'post' | 'connections';
  data: any;
}

interface CategoryStats {
  name: string;
  likes: number;
}

@Component({
  selector: 'app-feed',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    AvatarModule,
    DropdownModule,
    InputTextModule,
    DialogModule,
    AutoCompleteModule,
    PostComponent,
    RecommendedConnectionsComponent,
    AISummaryComponent,
    WelcomeMessageComponent,
    JobCardComponent,
  ],
  template: `
    <div class="feed-container">
      <div class="feed-header">
        <!-- Search - Full Width -->
        <div class="search-container-full">
          <p-autoComplete
            [(ngModel)]="searchQuery"
            [suggestions]="searchSuggestions"
            (completeMethod)="searchAutocomplete($event)"
            (onSelect)="onSearchSelect($event)"
            placeholder="Search or prompt to personalize your feed"
            [dropdown]="true"
            [minLength]="2"
            class="search-autocomplete"
          ></p-autoComplete>
        </div>

        <!-- Categories Row with Recommended Dropdown -->
        <div class="categories-row">
          <!-- Mobile: Dropdown Select -->
          <div class="category-select-mobile">
            <p-dropdown
              [options]="categoryDropdownOptions"
              [(ngModel)]="selectedCategoryOption"
              placeholder="Select a category"
              [showClear]="true"
              [appendTo]="'body'"
              optionLabel="label"
              optionValue="value"
              (ngModelChange)="onCategorySelectChange($event)"
              class="category-dropdown"
            ></p-dropdown>
          </div>

          <!-- Desktop: Top 3 Categories + All Categories Button -->
          <div class="category-boxes-desktop">
            <div
              *ngFor="let category of top3Categories"
              class="category-box"
              [class.active]="selectedCategory === category.name"
              (click)="onCategoryClick(category.name)"
            >
              <div class="category-name">{{ category.name }}</div>
              <div class="category-likes">
                <i class="pi pi-thumbs-up"></i>
                {{ category.likes }}
              </div>
            </div>
            <button
              pButton
              type="button"
              label="All Categories"
              icon="pi pi-list"
              (click)="showAllCategoriesDialog = true"
              class="all-categories-button"
            ></button>
          </div>

          <!-- Recommended Dropdown -->
          <div class="recommended-container">
            <p-dropdown
              [options]="recommendedOptions"
              [(ngModel)]="selectedRecommended"
              placeholder="Recommended"
              [showClear]="false"
              [appendTo]="'body'"
              (ngModelChange)="onRecommendedChange($event)"
              optionLabel="label"
              class="recommended-dropdown"
            ></p-dropdown>
          </div>
        </div>

        <!-- All Categories Dialog (Desktop) -->
        <p-dialog
          [(visible)]="showAllCategoriesDialog"
          header="Select Category"
          [modal]="true"
          [style]="{ width: '500px' }"
          [draggable]="false"
          [resizable]="false"
        >
          <div class="dialog-content">
            <p-dropdown
              [options]="categoryDropdownOptions"
              [(ngModel)]="selectedCategoryOption"
              placeholder="Select a category"
              [showClear]="true"
              [appendTo]="'body'"
              optionLabel="label"
              optionValue="value"
              (ngModelChange)="onCategorySelectChange($event)"
              class="category-dropdown-full"
              [style]="{ width: '100%' }"
            ></p-dropdown>
          </div>
          <ng-template pTemplate="footer">
            <button
              pButton
              type="button"
              label="Close"
              icon="pi pi-times"
              (click)="showAllCategoriesDialog = false"
              styleClass="p-button-text"
            ></button>
          </ng-template>
        </p-dialog>
      </div>

      <div class="feed-content">
        <app-welcome-message
          *ngIf="showWelcomeMessage"
          [message]="welcomeMessage"
          [recommendedCategory]="recommendedCategory"
          [onCategorySelect]="selectRecommendedCategory"
        ></app-welcome-message>

        <app-ai-summary
          *ngIf="aiSummary"
          [summary]="aiSummary"
        ></app-ai-summary>

        <!-- Leaders Section -->
        <div class="leaders-section" *ngIf="aiSummary && leaderOrganizations.length > 0">
          <div class="leaders-header">
            <h3 class="leaders-title">Leaders</h3>
          </div>
          <div class="leaders-grid">
            <div
              *ngFor="let leader of leaderOrganizations"
              class="leader-card"
              [routerLink]="['/organization', leader.id]"
            >
              <div class="leader-image-container">
                <img
                  *ngIf="leader.avatar"
                  [src]="leader.avatar"
                  [alt]="leader.name"
                  class="leader-image"
                />
                <div
                  *ngIf="!leader.avatar"
                  class="leader-avatar-placeholder"
                >
                  {{ leader.initials || 'O' }}
                </div>
              </div>
              <div class="leader-name">{{ leader.name }}</div>
            </div>
          </div>
        </div>

        <div class="jobs-section" *ngIf="selectedCategory && jobs.length > 0">
          <h3 class="jobs-section-title">Jobs in {{ selectedCategory }}</h3>
          <div class="jobs-grid">
            <app-job-card *ngFor="let job of jobs" [job]="job"></app-job-card>
          </div>
        </div>

        <ng-container *ngFor="let item of feedItems; let i = index">
          <app-post *ngIf="item.type === 'post'" [post]="item.data"></app-post>
          <app-recommended-connections
            *ngIf="item.type === 'connections'"
            [connections]="item.data"
          ></app-recommended-connections>
        </ng-container>

        <!-- Pathway Patty Category Recommendation -->
        <div
          class="patty-recommendation"
          *ngIf="!selectedCategory && !selectedSearchTerm && suggestedCategory"
        >
          <div class="patty-recommendation-header">
            <p-avatar
              label="PP"
              shape="circle"
              size="normal"
              styleClass="patty-avatar-small"
            ></p-avatar>
            <div class="patty-recommendation-title">
              <div class="patty-name">From Pathway Patty</div>
              <div class="patty-subtitle">Your personalized career guide</div>
            </div>
          </div>
          <div class="patty-recommendation-content">
            <p>
              Based on your interests, I think you might enjoy exploring
              <strong>{{ suggestedCategory }}</strong>.
              {{ getCategoryDescription(suggestedCategory) }}
            </p>
            <button
              pButton
              type="button"
              label="Explore {{ suggestedCategory }}"
              icon="pi pi-arrow-right"
              (click)="exploreCategory(suggestedCategory)"
              class="explore-button"
            ></button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .feed-container {
        width: 100%;
        max-width: 1200px;
        margin: 0 auto;
        padding: 1rem;
        background-color: #f5f5f5;
        min-height: calc(100vh - 60px);
        display: flex;
        flex-direction: column;
        align-items: center;
        box-sizing: border-box;
        overflow-x: hidden;
      }

      .feed-header {
        width: 100%;
        max-width: 1200px;
        margin-bottom: 1.5rem;
        padding: 1rem;
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        box-sizing: border-box;
        overflow-x: hidden;
        position: relative;
        z-index: 1;
      }

      /* Search - Full Width */
      .search-container-full {
        width: 100%;
        margin-bottom: 1rem;
      }

      .search-autocomplete {
        width: 100%;
      }

      /* Categories Row */
      .categories-row {
        display: flex;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;
      }

      .recommended-container {
        margin-left: auto;
      }

      .recommended-dropdown {
        min-width: 150px;
      }

      .filter-button {
        width: 40px;
        height: 40px;
        padding: 0;
      }

      /* Mobile Category Select */
      .category-select-mobile {
        display: none;
        width: 100%;
      }

      .category-dropdown {
        width: 100%;
      }

      /* Desktop Category Boxes */
      .category-boxes-desktop {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
        align-items: center;
      }

      .all-categories-button {
        min-width: 150px;
      }

      .category-boxes {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
      }

      .category-box {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 0.75rem 1.25rem;
        background-color: #f5f5f5;
        border: 2px solid transparent;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 150px;
      }

      .category-box:hover {
        background-color: #e8e8e8;
        border-color: #d0d0d0;
      }

      .category-box.active {
        background-color: #e3f2fd;
        border-color: #4a9eff;
      }

      .category-name {
        font-weight: 600;
        font-size: 0.95rem;
        color: #333333;
      }

      .category-likes {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #0066cc;
        font-weight: 500;
        font-size: 0.9rem;
      }

      .category-likes i {
        font-size: 0.85rem;
      }

      .feed-content {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        max-width: 1200px;
        margin: 0 auto;
        width: 100%;
        box-sizing: border-box;
        overflow-x: hidden;
      }

      :host ::ng-deep {
        .p-inputtext {
          width: 100%;
          box-sizing: border-box;
          max-width: 100%;
        }

        .p-dropdown {
          width: 100%;
          box-sizing: border-box;
          max-width: 100%;
        }

        .p-dropdown-panel {
          z-index: 1000 !important;
        }

        .p-autocomplete {
          width: 100%;
          box-sizing: border-box;
          max-width: 100%;
        }

        .p-autocomplete-input {
          width: 100%;
          max-width: 100%;
          padding: 0.75rem 1rem;
          font-size: 0.95rem;
          box-sizing: border-box;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        .p-autocomplete-panel {
          max-width: 100vw;
          box-sizing: border-box;
        }
      }

      .jobs-section {
        width: 100%;
        max-width: 1200px;
        margin-bottom: 2rem;
      }

      .jobs-section-title {
        font-size: 1.25rem;
        font-weight: 600;
        color: #000000;
        margin-bottom: 1rem;
      }

      .jobs-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 1rem;
      }

      .dialog-content {
        padding: 1rem 0;
      }

      .category-dropdown-full {
        width: 100%;
      }

      .leaders-section {
        background-color: #ffffff;
        border-radius: 8px;
        padding: 1.5rem;
        margin-bottom: 1rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .leaders-header {
        margin-bottom: 1rem;
      }

      .leaders-title {
        font-size: 1.25rem;
        font-weight: 600;
        color: #000000;
        margin: 0;
      }

      .leaders-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1.5rem;
      }

      .leader-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        cursor: pointer;
        transition: transform 0.2s ease;
        text-decoration: none;
        color: inherit;
      }

      .leader-card:hover {
        transform: translateY(-4px);
      }

      .leader-image-container {
        width: 120px;
        height: 120px;
        border-radius: 8px;
        overflow: hidden;
        margin-bottom: 0.75rem;
        background-color: #f5f5f5;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .leader-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .leader-avatar-placeholder {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #0066cc;
        color: #ffffff;
        font-size: 2.5rem;
        font-weight: 600;
      }

      .leader-name {
        font-size: 0.95rem;
        font-weight: 600;
        color: #000000;
        text-align: center;
        word-wrap: break-word;
      }

      .patty-recommendation {
        background-color: #ffffff;
        border-radius: 8px;
        padding: 1.5rem;
        margin-top: 1rem;
        margin-bottom: 1rem;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .patty-recommendation-header {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-bottom: 1rem;
        padding-bottom: 1rem;
        border-bottom: 1px solid #e5e5e5;
      }

      .patty-recommendation-title {
        flex: 1;
      }

      .patty-name {
        font-weight: 600;
        font-size: 1rem;
        color: #000000;
        margin-bottom: 0.25rem;
      }

      .patty-subtitle {
        font-size: 0.85rem;
        color: #666666;
      }

      .patty-recommendation-content {
        color: #333333;
        line-height: 1.6;
      }

      .patty-recommendation-content p {
        margin: 0 0 1rem 0;
      }

      .patty-recommendation-content strong {
        color: #0066cc;
        font-weight: 600;
      }

      .explore-button {
        background: #0066cc;
        color: #ffffff;
        border: none;
      }

      .explore-button:hover {
        background: #0052a3;
      }

      :host ::ng-deep {
        .patty-avatar-small {
          background-color: #ff6b6b;
          color: #ffffff;
          font-weight: 600;
        }
      }

      /* Responsive: Show mobile dropdown on small screens */
      @media (max-width: 767px) {
        .feed-container {
          padding: 0.75rem;
          padding-bottom: 4rem; /* Account for bottom navigation */
          min-height: calc(100vh - 50px - 60px); /* Header + bottom nav */
        }

        .feed-header {
          padding: 0.75rem;
          margin-bottom: 1rem;
        }

        .category-select-mobile {
          display: block;
          flex: 1;
          min-width: 0;
        }

        .category-boxes-desktop {
          display: none;
        }

        .recommended-container {
          margin-left: 0;
          width: 100%;
        }

        .categories-row {
          flex-direction: column;
          align-items: stretch;
        }

        .search-container-full {
          width: 100%;
          box-sizing: border-box;
        }

        .feed-content {
          padding-bottom: 1rem;
        }

        .leaders-grid {
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .leader-image-container {
          width: 100px;
          height: 100px;
        }

        .leader-avatar-placeholder {
          font-size: 2rem;
        }
      }

      /* Desktop: Hide mobile dropdown */
      @media (min-width: 768px) {
        .category-select-mobile {
          display: none;
        }

        .category-boxes-desktop {
          display: flex;
        }
      }
    `,
  ],
})
export class FeedComponent implements OnInit {
  posts: FeedPost[] = [];
  allPosts: FeedPost[] = [];
  recommendedConnections: RecommendedConnection[] = [];
  feedItems: FeedItem[] = [];
  searchQuery: string = '';
  searchSuggestions: string[] = [];
  aiSummary: string = '';
  selectedSearchTerm: string = '';
  selectedCategory: string = '';
  categories: CategoryStats[] = [];
  top3Categories: CategoryStats[] = [];
  categoryDropdownOptions: any[] = [];
  selectedCategoryOption: string = '';
  showAllCategoriesDialog: boolean = false;
  jobs: Job[] = [];
  welcomeMessage: string = '';
  recommendedCategory: string = '';
  leaderOrganizations: OrganizationDto[] = [];
  suggestedCategory: string = '';

  recommendedOptions = [
    { label: 'Recommended', value: 'recommended' },
    { label: 'Recent', value: 'recent' },
    { label: 'Popular', value: 'popular' },
    { label: 'My Posts', value: 'my-posts' },
    { label: 'Liked Posts', value: 'liked-posts' },
  ];
  selectedRecommended = { label: 'Recommended', value: 'recommended' };

  private searchSubject = new Subject<string>();

  constructor(
    private feedService: MockFeedService,
    private jobService: MockJobService,
    private authService: AuthService,
    private organizationService: OrganizationService
  ) {}

  ngOnInit() {
    this.loadPosts();
    this.loadRecommendedConnections();
    this.loadLeaderOrganizations();

    // Setup debounced search
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => this.feedService.searchAutocomplete(query))
      )
      .subscribe((suggestions) => {
        this.searchSuggestions = suggestions;
      });
  }

  loadPosts() {
    this.feedService.getPosts().subscribe((posts) => {
      this.allPosts = posts;
      this.posts = posts;
      this.calculateCategoryStats();
      this.generateWelcomeMessage();
      this.buildFeedItems();
    });
  }

  get showWelcomeMessage(): boolean {
    return (
      !this.selectedSearchTerm &&
      !this.selectedCategory &&
      !this.aiSummary &&
      this.welcomeMessage.length > 0
    );
  }

  calculateCategoryStats() {
    const categoryLikes: Record<string, number> = {};
    const allCategories = new Set<string>();

    // Collect all unique categories from posts
    this.allPosts.forEach((post) => {
      post.categories.forEach((category) => {
        allCategories.add(category);
        if (!categoryLikes.hasOwnProperty(category)) {
          categoryLikes[category] = 0;
        }
      });
    });

    // Count likes for posts the user has liked in each category
    this.allPosts.forEach((post) => {
      if (post.isLiked) {
        post.categories.forEach((category) => {
          if (categoryLikes.hasOwnProperty(category)) {
            categoryLikes[category] += post.likes;
          }
        });
      }
    });

    // Convert to array and sort by name
    this.categories = Array.from(allCategories)
      .map((category) => ({
        name: category,
        likes: categoryLikes[category] || 0,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Get top 3 categories sorted by likes (descending)
    this.top3Categories = [...this.categories]
      .sort((a, b) => b.likes - a.likes)
      .slice(0, 3);

    // Create dropdown options with category name and likes
    this.categoryDropdownOptions = this.categories.map((category) => ({
      label: `${category.name} (${category.likes} likes)`,
      value: category.name,
    }));

    // Add "All Categories" option at the beginning
    this.categoryDropdownOptions.unshift({
      label: 'All Categories',
      value: '',
    });

    // Initialize selected category option (value is the category name string)
    this.selectedCategoryOption = this.selectedCategory || '';
    
    // Suggest a category after categories are calculated
    this.suggestCategory();
  }

  generateWelcomeMessage() {
    // Get user's liked posts
    const likedPosts = this.allPosts.filter((post) => post.isLiked);

    if (likedPosts.length === 0) {
      // No likes yet - recommend a popular category
      this.recommendedCategory =
        this.categories.length > 0 ? this.categories[0].name : 'IT';

      const noLikesMessages = [
        `Welcome! I'm Pathway Patty, your career guide. I noticed you haven't liked any posts yet. Start exploring by checking out ${this.recommendedCategory} - it's a great place to begin your career journey!`,
        `Hey there! Pathway Patty here. Ready to discover your career path? I see you're just getting started. Why not explore ${this.recommendedCategory}? It's full of exciting opportunities waiting for you!`,
        `Hello! I'm Pathway Patty, and I'm here to help guide your career journey. Since you're new here, let me suggest starting with ${this.recommendedCategory} - it's a fantastic category to explore and find your passion!`,
        `Welcome to CareerPathway! I'm Pathway Patty, your friendly career companion. I notice you haven't explored much yet. Let's start your journey by diving into ${this.recommendedCategory} - you'll find amazing content there!`,
      ];

      this.welcomeMessage = this.getRandomMessage(noLikesMessages);
      return;
    }

    // Count likes per category
    const categoryCounts: Record<string, number> = {};
    likedPosts.forEach((post) => {
      post.categories.forEach((category) => {
        categoryCounts[category] = (categoryCounts[category] || 0) + 1;
      });
    });

    // Find the most liked category
    let topCategory = '';
    let maxLikes = 0;
    Object.keys(categoryCounts).forEach((category) => {
      if (categoryCounts[category] > maxLikes) {
        maxLikes = categoryCounts[category];
        topCategory = category;
      }
    });

    // Get top 3 categories
    const topCategories = Object.entries(categoryCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([category]) => category);

    // Generate personalized message
    if (topCategories.length === 1) {
      this.recommendedCategory = topCategories[0];

      const singleCategoryMessages = [
        `Hi there! I'm Pathway Patty, and I've been watching your interests. You've shown a strong interest in ${topCategories[0]}! Based on your likes, I think you'd love exploring more content in this category.`,
        `Welcome back! Pathway Patty here. I've noticed you're really into ${topCategories[0]} - that's awesome! There's so much more to discover in this field. Let's explore together!`,
        `Hello! I'm Pathway Patty, your career guide. Your interest in ${topCategories[0]} is really shining through! I've curated some great content in this category just for you.`,
        `Hey! Pathway Patty speaking. I can see ${topCategories[0]} is your thing! You've got great taste. Check out what else we have in this category - I think you'll love it!`,
      ];

      this.welcomeMessage = this.getRandomMessage(singleCategoryMessages);
    } else if (topCategories.length === 2) {
      this.recommendedCategory = topCategories[0];

      const twoCategoryMessages = [
        `Welcome back! I'm Pathway Patty, your career companion. I've noticed you're interested in ${topCategories[0]} and ${topCategories[1]}. Your passion for ${topCategories[0]} stands out - let's dive deeper into that!`,
        `Hi! Pathway Patty here. You've got diverse interests - I see you like both ${topCategories[0]} and ${topCategories[1]}! Your top interest is ${topCategories[0]}, and I think you'll find some amazing opportunities there.`,
        `Hello there! I'm Pathway Patty. I've been tracking your activity, and you're exploring ${topCategories[0]} and ${topCategories[1]}. Based on your engagement, ${topCategories[0]} seems to be your favorite - want to explore more?`,
        `Welcome! Pathway Patty speaking. You're showing interest in ${topCategories[0]} and ${topCategories[1]} - that's great! I'd recommend diving deeper into ${topCategories[0]} since that's where your passion really shows.`,
      ];

      this.welcomeMessage = this.getRandomMessage(twoCategoryMessages);
    } else {
      this.recommendedCategory = topCategories[0];
      const otherCategories = topCategories.slice(1).join(', ');

      const multipleCategoryMessages = [
        `Hello! Pathway Patty here. I've been tracking your interests, and you've shown enthusiasm for ${topCategories[0]}, ${otherCategories}, and more! Your top interest is ${topCategories[0]} - I think you'll find some amazing opportunities there.`,
        `Welcome back! I'm Pathway Patty. You're exploring quite a range - ${topCategories[0]}, ${otherCategories}, and beyond! Your strongest interest appears to be ${topCategories[0]}. Let me show you what's trending there!`,
        `Hi there! Pathway Patty speaking. I love your diverse interests! You're into ${topCategories[0]}, ${otherCategories}, and more. But ${topCategories[0]} really stands out as your favorite - want to see what's new?`,
        `Hey! I'm Pathway Patty, your career guide. You've got great taste across ${topCategories[0]}, ${otherCategories}, and other fields! Based on your activity, ${topCategories[0]} is your top pick. Ready to explore more?`,
      ];

      this.welcomeMessage = this.getRandomMessage(multipleCategoryMessages);
    }
  }

  private getRandomMessage(messages: string[]): string {
    const randomIndex = Math.floor(Math.random() * messages.length);
    return messages[randomIndex];
  }

  selectRecommendedCategory = (category?: string) => {
    const categoryToSelect = category || this.recommendedCategory;
    if (categoryToSelect) {
      this.onCategoryClick(categoryToSelect);
    }
  };

  loadRecommendedConnections() {
    this.feedService.getRecommendedConnections().subscribe((connections) => {
      this.recommendedConnections = connections;
      this.buildFeedItems();
    });
  }

  loadLeaderOrganizations() {
    // Get top 3 organizations by followers count
    // In a real app, this would come from the organization service
    const leaderIds = [
      'org-medcare',
      'org-healthfirst',
      'org-techvantage',
    ];

    const leaderObservables = leaderIds.map((id) =>
      this.organizationService.getOrganization(id)
    );

    forkJoin(leaderObservables).subscribe({
      next: (organizations) => {
        this.leaderOrganizations = organizations.filter(
          (org) => org !== undefined
        ) as OrganizationDto[];
      },
      error: (err) => {
        console.error('Failed to load leader organizations', err);
      },
    });
  }

  searchAutocomplete(event: any) {
    const query = event.query;
    this.searchSubject.next(query);
  }

  onSearchSelect(event: AutoCompleteSelectEvent) {
    const selected = event.value;
    if (!selected) {
      this.clearSearch();
      return;
    }

    this.selectedSearchTerm = selected;
    this.searchQuery = selected;
    this.selectedCategory = ''; // Clear category selection when searching
    this.jobs = []; // Clear jobs when searching
    this.welcomeMessage = ''; // Clear welcome message when searching

    // Load AI summary
    this.feedService.getAISummary(selected).subscribe((summary) => {
      this.aiSummary = summary;
    });

    // Filter posts based on search
    this.feedService.searchPosts(selected).subscribe((filteredPosts) => {
      this.posts = filteredPosts;
      this.buildFeedItems();
    });
  }

  onCategorySelectChange(categoryName: string) {
    // ngModelChange passes the value directly (category name string) when using optionValue
    this.selectCategory(categoryName || '');
    // Close dialog if open (desktop)
    if (this.showAllCategoriesDialog) {
      this.showAllCategoriesDialog = false;
    }
  }

  onCategoryClick(categoryName: string) {
    // Toggle category selection
    if (this.selectedCategory === categoryName) {
      this.selectCategory('');
      // Update dropdown selection
      this.selectedCategoryOption = '';
    } else {
      this.selectCategory(categoryName);
      // Update dropdown selection (value is the category name string)
      this.selectedCategoryOption = categoryName;
    }
  }

  private selectCategory(categoryName: string) {
    // Clear search if active
    if (!categoryName) {
      this.selectedCategory = '';
      this.selectedCategoryOption = ''; // Keep dropdown in sync
      this.searchQuery = '';
      this.selectedSearchTerm = '';
      this.aiSummary = '';
      this.jobs = [];
      this.posts = this.allPosts;
      this.generateWelcomeMessage(); // Regenerate welcome message when clearing category
      this.buildFeedItems();
      return;
    }

    this.selectedCategory = categoryName;
    this.selectedCategoryOption = categoryName; // Keep dropdown in sync
    this.searchQuery = '';
    this.selectedSearchTerm = '';
    this.welcomeMessage = ''; // Clear welcome message when selecting category

    // Load AI summary for category
    this.feedService.getAISummary(categoryName).subscribe((summary) => {
      this.aiSummary = summary;
    });

    // Load jobs for category
    this.jobService.getJobsByCategory(categoryName).subscribe((jobs) => {
      this.jobs = jobs;
    });

    // Filter posts by category
    this.posts = this.allPosts.filter((post) =>
      post.categories.includes(categoryName)
    );
    this.buildFeedItems();
  }

  clearSearch() {
    this.searchQuery = '';
    this.selectedSearchTerm = '';
    this.selectedCategory = '';
    this.selectedCategoryOption = '';
    this.aiSummary = '';
    this.jobs = [];
    this.posts = this.allPosts;
    this.generateWelcomeMessage(); // Regenerate welcome message when clearing search
    this.buildFeedItems();
  }

  buildFeedItems() {
    if (this.posts.length === 0 || this.recommendedConnections.length === 0) {
      return;
    }

    this.feedItems = [];

    // Insert recommended connections after the 2nd post
    const connectionsInsertIndex = 2;

    this.posts.forEach((post, index) => {
      this.feedItems.push({ type: 'post', data: post });

      // Insert recommended connections after the 2nd post
      if (index === connectionsInsertIndex - 1) {
        this.feedItems.push({
          type: 'connections',
          data: this.recommendedConnections,
        });
      }
    });
  }

  onRecommendedChange(selected: any) {
    if (!selected || !selected.value) {
      return;
    }

    const value = selected.value;
    
    // Clear category and search when changing recommended filter
    this.selectedCategory = '';
    this.selectedCategoryOption = '';
    this.searchQuery = '';
    this.selectedSearchTerm = '';
    this.aiSummary = '';
    this.jobs = [];
    this.welcomeMessage = '';

    switch (value) {
      case 'recommended':
        // Show all posts (default)
        this.posts = this.allPosts;
        this.generateWelcomeMessage();
        break;
      
      case 'recent':
        // Show posts sorted by date (newest first)
        this.posts = [...this.allPosts].sort(
          (a, b) => b.date.getTime() - a.date.getTime()
        );
        break;
      
      case 'popular':
        // Show posts sorted by likes (most liked first)
        this.posts = [...this.allPosts].sort(
          (a, b) => b.likes - a.likes
        );
        break;
      
      case 'my-posts':
        // Show posts by current user
        const profile = this.authService.getCurrentProfile();
        if (profile) {
          const currentUserName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.username || '';
          this.posts = this.allPosts.filter(
            (post) => post.author.name === currentUserName
          );
        } else {
          this.posts = [];
        }
        break;
      
      case 'liked-posts':
        // Show posts the user has liked
        this.posts = this.allPosts.filter((post) => post.isLiked);
        break;
      
      default:
        this.posts = this.allPosts;
        break;
    }

    this.buildFeedItems();
  }

  suggestCategory() {
    // Wait for categories to be calculated
    if (this.categories.length === 0) {
      return;
    }

    // Get user's liked posts to understand their interests
    const likedPosts = this.allPosts.filter((post) => post.isLiked);
    
    // Get all categories the user has already engaged with
    const engagedCategories = new Set<string>();
    likedPosts.forEach((post) => {
      post.categories.forEach((category) => {
        engagedCategories.add(category);
      });
    });

    // Find a category they haven't explored yet
    const allCategories = this.categories.map((c) => c.name);
    const unengagedCategories = allCategories.filter(
      (category) => !engagedCategories.has(category)
    );

    if (unengagedCategories.length > 0) {
      // Suggest a random unengaged category
      this.suggestedCategory =
        unengagedCategories[
          Math.floor(Math.random() * unengagedCategories.length)
        ];
    } else if (allCategories.length > 0) {
      // If they've engaged with all categories, suggest a less popular one
      const sortedByLikes = [...this.categories].sort(
        (a, b) => a.likes - b.likes
      );
      this.suggestedCategory = sortedByLikes[0]?.name || allCategories[0];
    } else {
      // Fallback to a default category
      this.suggestedCategory = 'IT';
    }
  }

  getCategoryDescription(category: string): string {
    const descriptions: Record<string, string> = {
      IT: 'This field is full of exciting opportunities in software development, cloud computing, and emerging technologies. Discover cutting-edge innovations and career paths in tech.',
      Construction:
        'Explore the world of building and infrastructure. From sustainable construction practices to innovative project management, this category offers insights into shaping our physical world.',
      Medical:
        'Dive into healthcare innovation and medical advancements. Learn about telemedicine, precision medicine, and the latest developments improving patient care.',
      Manufacturing:
        'Discover how modern manufacturing is evolving with automation, AI, and sustainable practices. Explore careers in industrial innovation and production excellence.',
      Finance:
        'Navigate the financial landscape with insights into investment strategies, fintech innovations, and wealth management. Learn about building financial security and growth.',
      Education:
        'Explore educational technology and learning innovations. From online learning platforms to vocational training, discover how education is transforming.',
      Agriculture:
        'Learn about sustainable farming, agricultural technology, and food systems. Discover how modern agriculture is feeding the world while protecting our planet.',
      Retail:
        'Explore the evolving retail landscape with omnichannel strategies, e-commerce innovations, and customer experience enhancements.',
    };

    return (
      descriptions[category] ||
      `This category offers diverse opportunities and insights. Explore to discover what interests you most!`
    );
  }

  exploreCategory(category: string) {
    this.selectCategory(category);
  }
}
